import { useRef } from 'react';

interface CacheItem<T> {
  data: T;
  timestamp: number;
}

interface CacheOptions {
  duration?: number;
  timeout?: number;
}

interface CacheHookReturn<T> {
  getCachedData: (key: string) => T | null;
  setCachedData: (key: string, data: T) => void;
  isRequestInProgress: (key: string) => boolean;
  setRequestInProgress: (key: string, inProgress: boolean) => void;
  waitForRequest: (key: string) => Promise<void>;
}

export function useCache<T>(options: CacheOptions = {}): CacheHookReturn<T> {
  const { 
    duration = 5 * 60 * 1000, 
    timeout = 30 * 1000 
  } = options;
  
  const cacheRef = useRef<Map<string, CacheItem<T>>>(new Map());
  const requestsInProgressRef = useRef<Set<string>>(new Set());

  const getCachedData = (key: string): T | null => {
    const cached = cacheRef.current.get(key);
    if (!cached) return null;
    
    const now = Date.now();
    if (now - cached.timestamp > duration) {
      cacheRef.current.delete(key);
      return null;
    }
    
    return cached.data;
  };

  const setCachedData = (key: string, data: T): void => {
    cacheRef.current.set(key, {
      data,
      timestamp: Date.now()
    });
  };

  const isRequestInProgress = (key: string): boolean => {
    return requestsInProgressRef.current.has(key);
  };

  const setRequestInProgress = (key: string, inProgress: boolean): void => {
    if (inProgress) {
      requestsInProgressRef.current.add(key);
    } else {
      requestsInProgressRef.current.delete(key);
    }
  };

  const waitForRequest = async (key: string): Promise<void> => {
    const startTime = Date.now();
    
    while (requestsInProgressRef.current.has(key) && (Date.now() - startTime) < timeout) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    if (requestsInProgressRef.current.has(key)) {
      console.warn(`${key} timeout, retry`);
      requestsInProgressRef.current.delete(key);
    }
  };

  return {
    getCachedData,
    setCachedData,
    isRequestInProgress,
    setRequestInProgress,
    waitForRequest
  };
}

export function useAsyncCache<T>(options: CacheOptions = {}) {
  const cache = useCache<T>(options);
  
  const fetchWithCache = async <R extends T>(
    key: string,
    fetchFn: () => Promise<R>,
    logPrefix?: string
  ): Promise<R | null> => {
    const cachedData = cache.getCachedData(key);
    if (cachedData) {
      if (logPrefix) {
        console.info(`use cache ${logPrefix}`);
      }
      return cachedData as R;
    }

    if (cache.isRequestInProgress(key)) {
      if (logPrefix) {
        console.info(`${logPrefix} in request`);
      }
      await cache.waitForRequest(key);
      
      const newCachedData = cache.getCachedData(key);
      if (newCachedData) {
        return newCachedData as R;
      }
    }

    cache.setRequestInProgress(key, true);
    if (logPrefix) {
      console.info(`request ${logPrefix}`);
    }
    
    try {
      const data = await fetchFn();
      cache.setCachedData(key, data);
      return data;
    } catch (error) {
      if (logPrefix) {
        console.error(`Error fetching ${logPrefix}:`, error);
      }
      return null;
    } finally {
      cache.setRequestInProgress(key, false);
    }
  };

  return {
    fetchWithCache,
    ...cache
  };
}